'use client';

import React, { useState } from 'react';
import { cn } from '@shared/utils/utils';

interface TagsProps {
  children: React.ReactNode;
  onRemove?: () => void;
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
}

/**
 * 🎯 Tags - Componente de etiquetas con funcionalidad de eliminación
 *
 * Muestra etiquetas con apariencia de badge que al hacer hover
 * muestran una X para permitir su eliminación.
 *
 * Características:
 * - Apariencia de badge redondeado
 * - Hover muestra X de eliminación
 * - Callback para manejar eliminación
 * - Estados disabled y enabled
 * - Responsive y adaptable
 */
const Tags = React.memo(
  ({
    children,
    onRemove,
    className,
    dataTestId,
    disabled = false,
  }: TagsProps) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = () => {
      if (!disabled) {
        setIsHovered(true);
      }
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
    };

    const handleRemove = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onRemove && !disabled) {
        onRemove();
      }
    };

    return (
      <div
        className={cn(
          'inline-flex items-center gap-0 px-3 py-0.5 rounded-full text-xs bg-card-border text-text transition-all duration-200 h-7',
          'hover:bg-card-border/80',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && 'cursor-pointer',
          className
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid={dataTestId || 'tags'}
      >
        {/* 🎯 Contenido del tag */}
        <span className="text-xs">{children}</span>

        {/* 🎯 Icono X de eliminación */}
        {onRemove && !disabled && (
          <span
            className={cn(
              'flex text-text transition-all duration-200 cursor-pointer text-xl font-medium leading-none translate-y-[-2px] overflow-hidden',
              isHovered ? 'opacity-100 w-4' : 'opacity-0 w-0'
            )}
            onClick={handleRemove}
            data-testid={`${dataTestId || 'tags'}-remove`}
          >
            <span className="inline-block w-3">&nbsp;</span>&times;
          </span>
        )}
      </div>
    );
  }
);

Tags.displayName = 'Tags';

export default Tags;
